<template>
  <div class="pm-mobile">
    <!-- Action buttons -->
    <div class="container-fluid px-0 my-2">
      <div class="row g-2">
        <div class="col-4">
          <button @click="refresh()"
            class="btn btn-primary form-control"
          >
            <i class="fa fa-sync"></i> Refresh
          </button>
        </div>
        <div class="col-4">
          <button @click="newRecord()"
            class="btn btn-success form-control"
          >
            <i class="fa fa-plus"></i> New
          </button>
        </div>
        <div class="col-4">
          <button @click="filterVisible = true"
            class="btn btn-outline-secondary form-control"
          >
            <i class="fa fa-filter"></i> Filter
          </button>
        </div>
      </div>
    </div>

    <hr class="my-2" />

    <!-- Mobile-optimized list view -->
    <div class="mobile-pm-list">
      <div v-for="row in rows" :key="row.Id" class="pm-card mb-2" @click="editRecord(row.Id)">
        <div class="card">
          <div class="card-body p-2">
            <div class="d-flex justify-content-between align-items-start mb-2">
              <div class="flex-grow-1">
                <strong>PM #{{ row.Id }}</strong>
                <div class="text-muted small">{{ row.Contact?.Company || 'N/A' }}</div>
              </div>
              <div class="text-end ms-2">
                <span v-if="row.CompletionStatus" :class="getStatusBadgeClass(row.CompletionStatus)" class="badge">
                  {{ row.CompletionStatus }}
                </span>
              </div>
            </div>
            
            <div class="small mb-1" v-if="row.Name">
              <strong>{{ row.Name }}</strong>
            </div>
            
            <div class="small mb-1" v-if="row.PrimaryTechnician?.TechnicianName">
              <i class="fas fa-user me-1"></i>{{ row.PrimaryTechnician.TechnicianName }}
            </div>
            
            <div class="row small mb-1">
              <div class="col-6" v-if="row.LastCompletedDate">
                <i class="fas fa-check-circle me-1"></i>{{ getLocalDate(row.LastCompletedDate) }}
              </div>
              <div class="col-6 text-end" v-if="row.NextDueDate">
                <i class="fas fa-calendar me-1"></i>Due: {{ getLocalDate(row.NextDueDate) }}
              </div>
            </div>
            
            <div class="small" v-if="row.Frequency">
              <i class="fas fa-redo me-1"></i>{{ row.Frequency }}
            </div>
            
            <div class="small text-truncate text-muted" v-if="row.Description">
              {{ row.Description }}
            </div>
          </div>
        </div>
      </div>
      
      <!-- Empty state -->
      <div v-if="rows.length === 0" class="text-center text-muted py-5">
        <i class="fas fa-tools fa-3x mb-3"></i>
        <p>No PMs found</p>
      </div>
    </div>

    <!-- Filter Modal (placeholder - add PM filter component when available) -->
    <kt-modal 
      :visible="filterVisible" 
      title="Filter PMs" 
      @dismiss="filterVisible = false"
    >
      <template #body>
        <div class="p-3">
          <p class="text-muted">PM filters will be added here</p>
          <button @click="filterVisible = false" class="btn btn-secondary">Close</button>
        </div>
      </template>
    </kt-modal>
  </div>
</template>

<style scoped>
.pm-mobile {
  padding: 0.5rem;
}

.pm-card {
  cursor: pointer;
  transition: transform 0.2s;
}

.pm-card:active {
  transform: scale(0.98);
}

.pm-card .card {
  border-left: 3px solid #6610f2;
}

.mobile-pm-list {
  max-height: calc(100vh - 200px);
  overflow-y: auto;
}
</style>

<script setup lang="ts">
import { ref } from "vue";
import Helper from "@/services/Helper";
import KtModal from "@/components/UI/KtModal.vue";

// Props - receive data from parent
const props = defineProps<{
  rows: any[];
  total: number;
  filter?: any;
}>();

// Emits
const emit = defineEmits<{
  refresh: [];
  newRecord: [];
  editRecord: [id: number];
  setFilter?: [filter: any];
}>();

const filterVisible = ref(false);

// Methods
const refresh = () => {
  emit('refresh');
};

const newRecord = () => {
  emit('newRecord');
};

const editRecord = (id: number) => {
  emit('editRecord', id);
};

const getLocalDate = (val: any) => {
  if (!val) return '';
  let tmp = new Date(val);
  return Helper.dateToInput(tmp, Helper.DateFormat_Date);
};

const getStatusBadgeClass = (status: string) => {
  const statusLower = status?.toLowerCase() || '';
  
  if (statusLower.includes('completed') || statusLower.includes('done')) {
    return 'bg-success';
  } else if (statusLower.includes('pending') || statusLower.includes('scheduled')) {
    return 'bg-warning';
  } else if (statusLower.includes('progress') || statusLower.includes('active')) {
    return 'bg-primary';
  } else if (statusLower.includes('overdue')) {
    return 'bg-danger';
  } else {
    return 'bg-secondary';
  }
};
</script>
